<template>
  <BModal
    id="modal-setup-billing"
    no-close-on-esc
    no-close-on-backdrop
    hide-header-close
    hide-footer
    hide-header
    size="md"
    :centered="true"
  >
    <validation-observer
      ref="observer"
      v-slot="{ invalid }"
    >
      <div class="text-left my-1 d-flex flex-column gap-4">
        <!-- Header Title changes by tab -->
        <div class="font-bold text-10 text-black">
          {{
            currentTab === 'duration'
              ? 'Set Durasi Langganan'
              : 'Set Biaya Langganan'
          }}
        </div>

        <p class="text-[#626262]">
          Silakan setting dan tetapkan
          {{ currentTab === 'duration' ? 'durasi' : 'biaya' }} langganan untuk
          pengguna.
        </p>

        <!-- Tabs -->
        <BTabs
          v-model="tabIndex"
          nav-class="!border-b !border-[#E5E5E5]"
          active-nav-item-class="!border-b-2 !border-[#FD4F2F]"
        >
          <BTab
            title="Durasi"
            @click="switchTab('duration')"
          >
            <!-- Durasi Langganan Options -->
            <b-form-group
              label="Durasi Langganan"
              label-class="text-base font-medium text-black"
            >
              <div class="grid grid-cols-3 gap-2">
                <b-button
                  v-for="duration in durationDefaults"
                  :key="`default-${duration}`"
                  :variant="isSelected(duration) ? 'primary' : 'outline-primary'"
                  class="px-[12px] py-[8px] rounded border text-sm"
                  @click="toggleDuration(duration)"
                >
                  {{ duration }} Hari
                </b-button>
              </div>
            </b-form-group>

            <!-- Custom Duration Input -->
            <div class="my-2">
              <h3 class="text-base text-black">
                Input Custom
              </h3>
              <b-col
                md="12"
                class="p-0"
              >
                <b-form-group label="Durasi Langganan">
                  <validation-provider
                    #default="{ errors }"
                    name="Durasi Langganan"
                    :rules="{
                      min_value: 1,
                      max_value: 365,
                    }"
                  >
                    <div class="flex items-center gap-2">
                      <b-form-input
                        v-model="customDuration"
                        :maxlength="3"
                        placeholder="Masukkan durasi"
                        class="w-full"
                      />
                      <span class="text-[#626262]">Hari</span>
                    </div>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-button
                type="button"
                variant="outline-primary"
                @click="addCustomDuration"
              >
                + Tambah Durasi Langganan
              </b-button>
            </div>

            <!-- Bottom Buttons -->
            <div class="d-flex gap-4 justify-center">
              <button
                type="button"
                class="w-100 button-secondary"
                @click="$bvModal.hide('modal-setup-billing'), resetState()"
              >
                Batal
              </button>
              <button
                type="button"
                class="w-100 button-primary"
                :disabled="selectedDurations.length === 0"
                @click="switchTab('cost')"
              >
                Berikutnya
              </button>
            </div>
          </BTab>

          <BTab
            title="Biaya"
            :disabled="selectedDurations.length === 0"
            @click="switchTab('cost')"
          >
            <!-- Biaya inputs generated from selected durations -->
            <div
              v-for="duration in sortedSelectedDurations"
              :key="`cost-${duration}`"
            >
              <b-form-group>
                <template #label>
                  <div class="flex justify-between">
                    <div class="text-black flex items-center">
                      <span class="text-base">Nominal {{ duration }} Hari</span>
                      <strong class="text-primary">*</strong>
                    </div>
                  </div>
                </template>
                <validation-provider
                  #default="{ errors }"
                  :name="`Nominal ${duration} Hari`"
                  rules="required"
                >
                  <b-form-input
                    :value="formattedNominalByDuration[duration]"
                    :maxlength="11"
                    min="0"
                    placeholder="Set nominal biaya langganan"
                    :state="errors.length > 0 ? false : null"
                    @keypress="isNumber($event)"
                    @input="onNominalChange(duration, $event)"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </div>

            <!-- Bottom Buttons -->
            <div class="d-flex gap-4 justify-center">
              <button
                type="button"
                class="w-100 button-secondary"
                @click="$bvModal.hide('modal-setup-billing'), resetState()"
              >
                Batal
              </button>
              <button
                type="button"
                class="w-100 button-primary"
                :disabled="invalid || !canSubmit"
                @click="handleSubmit"
              >
                Submit
              </button>
            </div>
          </BTab>
        </BTabs>
      </div>
    </validation-observer>
  </BModal>
</template>

<script>
import {
  BModal,
  BTabs,
  BTab,
  BCol,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  required, min, max,
} from '@validations'
import { isNumber } from '@/libs/helpers'

export default {
  name: 'ModalSetupBilling',

  components: {
    BModal,
    BTabs,
    BTab,
    BCol,
    BFormGroup,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
  },

  /**
   * Component props
   * @property {Array} subscriptionInfo - Existing subscription information for auto-fill
   */
  props: {
    subscriptionInfo: {
      type: Array,
      default: null,
    },
  },
  /**
   * Component data initialization
   * @returns {Object} Component data state
   */
  data() {
    return {
      // Validation rules helpers
      required,
      min,
      max,

      isNumber,

      // UI state
      tabIndex: 0,
      currentTab: 'duration',

      // Duration options and selections
      durationDefaults: [30, 60, 90],
      selectedDurations: [],
      customDuration: null,
      nominalByDuration: {},
      formattedNominalByDuration: {},
    }
  },
  computed: {
    /**
     * Get sorted selected durations (ascending order)
     * @returns {number[]}
     */
    sortedSelectedDurations() {
      return [...this.selectedDurations].sort((a, b) => a - b)
    },

    /**
     * Check if form can be submitted
     * @returns {boolean}
     */
    canSubmit() {
      if (this.selectedDurations.length === 0) return false
      return this.selectedDurations.every(d => {
        const val = this.nominalByDuration[d]
        return typeof val === 'number' && !Number.isNaN(val) && val > 0
      })
    },
  },
  watch: {
    /**
     * Watch for subscriptionInfo changes and auto-fill form
     */
    subscriptionInfo: {
      handler(newSubscriptionInfo) {
        if (newSubscriptionInfo && newSubscriptionInfo.length > 0) {
          this.handleSubscriptionInfoChange(newSubscriptionInfo)
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    formatNumber(value) {
      const stringValue = String(value)
      const cleanValue = stringValue.replace(/\D/g, '')
      return cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    unformatNumber(formattedValue) {
      return Number(formattedValue.replace(/\D/g, '')) || 0
    },
    onNominalChange(duration, event) {
      const rawValue = event

      const numericValue = this.unformatNumber(rawValue)

      this.$set(this.nominalByDuration, duration, numericValue)

      const formattedValue = this.formatNumber(numericValue)
      this.$set(this.formattedNominalByDuration, duration, formattedValue)
    },
    /** Reset internal state on modal close */
    resetState() {
      this.tabIndex = 0
      this.currentTab = 'duration'
      this.selectedDurations = []
      this.customDuration = null
      this.nominalByDuration = {}
      this.formattedNominalByDuration = {}
    },
    /** Switch active tab and update header title */
    switchTab(key) {
      this.currentTab = key
      this.tabIndex = key === 'duration' ? 0 : 1
    },
    /** Check if a duration is currently selected */
    isSelected(duration) {
      return this.selectedDurations.includes(duration)
    },
    /** Toggle a duration selection */
    toggleDuration(duration) {
      if (this.isSelected(duration)) {
        this.selectedDurations = this.selectedDurations.filter(item => item !== duration)
        this.$delete(this.nominalByDuration, duration)
      } else {
        this.selectedDurations.push(duration)
      }
    },
    /** Add a custom duration after validation */
    addCustomDuration() {
      const value = Number(this.customDuration)
      if (!value || value <= 0) return
      if (!this.selectedDurations.includes(value)) {
        this.selectedDurations.push(value)
      }
      // Move to costs tab automatically like reference flow
      this.switchTab('cost')
    },
    handleSubmit() {
      if (!this.canSubmit) return
      // eslint-disable-next-line camelcase
      const subscription_data = this.selectedDurations.map(duration => ({
        duration_day: duration,
        price: this.nominalByDuration[duration],
      }))
      const payload = {
        subscription_data,
      }
      this.$emit('submitted', payload)
    },

    /**
     * Handle subscription info changes and auto-fill form
     * @param {Object} subscriptionData - Subscription data from API
     */
    handleSubscriptionInfoChange(subscriptionData) {
      try {
        if (subscriptionData.length === 0) {
          // No existing subscription data
          this.resetFormState()
          return
        }

        // Auto-fill form with existing data
        this.autoFillForm(subscriptionData)
        this.hasSubscriptionData = true
      } catch (error) {
        this.resetFormState()
      }
    },
    /**
     * Auto-fill form with subscription data
     * @param {Array} subscriptionData - Array of subscription items
     */
    autoFillForm(subscriptionData) {
      // Reset current form state
      this.resetFormState()

      // Extract durations and prices
      subscriptionData.forEach(item => {
        const { duration_day, price } = item

        // Add duration to selected durations
        if (!this.selectedDurations.includes(duration_day)) {
          this.selectedDurations.push(duration_day)
        }

        // Set nominal values
        this.$set(this.nominalByDuration, duration_day, price)
        this.$set(this.formattedNominalByDuration, duration_day, this.formatNumber(price))
      })

      // Switch to cost tab to show filled values
      this.switchTab('cost')
    },

    /**
     * Reset form to initial state
     */
    resetFormState() {
      this.selectedDurations = []
      this.nominalByDuration = {}
      this.formattedNominalByDuration = {}
      this.customDuration = null
      this.tabIndex = 0
      this.currentTab = 'duration'
    },
  },
}
</script>
